// Demo data: Preference Center research (Demo E — Build 15)
// Seeded dataset for the 5th industry demo: preference-center-research

import type { Segment } from '@/lib/types'
import type { EvidenceRow } from '@/lib/ensemble/types'

export const DEMO_PROJECT_ID = 'proj_pref_center_demo'
export const DEMO_TENANT_ID = 'tnt_demo'
export const DEMO_WORKSPACE_ID = 'wks_demo'

export const DEMO_RESEARCH_QUESTION =
  'Which preference-center category framing reduces global opt-outs most across customer segments, and which segments show the biggest dissent?'

export const DEMO_PRE_READ_QUESTION = DEMO_RESEARCH_QUESTION

export const DEMO_STIMULI: Array<{ stimulus_id: string; label: string; content: string; type: string }> = [
  { stimulus_id: 'stim_a', label: 'Approach A', content: 'Granular sub-topic categories: Promotions, New products, Service updates, Account security, Regulatory notices, Partner offers, Research & surveys, Event invitations, Rate changes, Statements, Policy updates, Rewards', type: 'framing' },
  { stimulus_id: 'stim_b', label: 'Approach B', content: 'Broader topical groupings: Your account & security, Offers & promotions, Product & service updates, Research & feedback, Regulatory & policy', type: 'framing' },
  { stimulus_id: 'stim_c', label: 'Approach C', content: 'Behavioral lifecycle framing: Keep me informed about my account, Let me know about relevant offers, Ask for my input, Required communications only', type: 'framing' },
]

export const DEMO_SEGMENTS: Segment[] = [
  {
    segment_id: 'seg_general',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_pref_center_demo',
    name: 'General customers',
    respondent_count: 812,
    dominant_themes: ['convenience', 'simplicity', 'control'],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I just want it to be simple. Too many categories and I\'ll opt out of everything.', source_file: 'pref-center-survey.csv', source_row: 14 },
      { text: 'Five categories is about right for me. I can actually tell which one I care about.', source_file: 'pref-center-survey.csv', source_row: 88 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_high_engagement',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_pref_center_demo',
    name: 'High-engagement / under-35',
    respondent_count: 235,
    dominant_themes: ['granularity', 'control', 'customization'],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I actually want 12 categories. I want to opt in to rate changes and policy updates separately.', source_file: 'pref-center-survey.csv', source_row: 201 },
      { text: 'The more granular the better — I hate when companies lump things together and then send me junk.', source_file: 'pref-center-survey.csv', source_row: 314 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_recent_opt_outs',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_pref_center_demo',
    name: 'Recent opt-outs',
    respondent_count: 153,
    dominant_themes: ['overwhelm', 'irrelevance', 'distrust'],
    evidence_strength: 'Medium',
    representative_quotes: [
      { text: 'I already opted out of everything because there were too many options and I didn\'t trust which ones were real.', source_file: 'pref-center-survey.csv', source_row: 892 },
      { text: 'I want fewer categories but with clearer language about what I\'m actually signing up for.', source_file: 'pref-center-survey.csv', source_row: 1031 },
    ],
    created_at: 1700000000000,
  },
]

export const DEMO_TWIN_PANEL_READINESS = {
  total_twins: 1200,
  twins_validated: 1089,
  twins_low_confidence: 81,
  twins_excluded: 30,
  mean_accuracy: 0.73,
  wilson_lower: 0.71,
  wilson_upper: 0.75,
  p25_accuracy: 0.62,
  p50_accuracy: 0.74,
  p75_accuracy: 0.83,
  p90_accuracy: 0.89,
}

export const DEMO_BULK_QUERY_RESULT = {
  query_id: 'bq_pref_center_demo',
  twins_used: 1089,
  twins_excluded: 111,
  distribution: { 'Approach A': 0.31, 'Approach B': 0.62, 'Approach C': 0.07 },
  per_segment: {
    'General customers': { 'Approach A': 0.24, 'Approach B': 0.68, 'Approach C': 0.08 },
    'High-engagement / under-35': { 'Approach A': 0.51, 'Approach B': 0.42, 'Approach C': 0.07 },
    'Recent opt-outs': { 'Approach A': 0.27, 'Approach B': 0.66, 'Approach C': 0.07 },
  },
  ensemble_consensus: 0.84,
  ensemble_diversity: 0.16,
  confidence_band: { lower: 0.59, upper: 0.65 },
  headline: 'Approach B reduces opt-outs 6 points across the panel — but Approach A wins on the high-engagement segment',
}

// APPROVED_HEADLINE must match DEMO_APPROVED_HEADLINES in marketing demo carousel
export const APPROVED_HEADLINE = DEMO_BULK_QUERY_RESULT.headline

export const DEMO_EVIDENCE: EvidenceRow[] = DEMO_SEGMENTS.flatMap((seg, segIdx) =>
  (seg.representative_quotes ?? []).map((q, i) => ({
    respondent_id: `resp_${seg.segment_id}_${i}`,
    segment: seg.name,
    response: seg.dominant_themes?.[i % (seg.dominant_themes?.length ?? 1)],
    open_text: q.text,
    source_file: 'pref-center-survey.csv',
    row_index: segIdx * 5 + i,
  }))
)
